import { on } from "@ngrx/store";
import { SuperUserState } from "../../super-user.state";
import { InternalUsersActions } from "./internal-users.actions";
import { PagesStore } from "../../../../pages.state";

// **************************************************************************
// HELPER: Update superUser state within PagesStore
// **************************************************************************

function withSuperUser(state: PagesStore, updater: (superUser: SuperUserState) => SuperUserState): PagesStore {
    return { ...state, superUser: updater(state.superUser) };
}

// **************************************************************************
// USERS
// **************************************************************************

const loadUsers = on(InternalUsersActions.loadUsers, (state: PagesStore) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        loading: { ...superUserState.internalUsers.loading, loading: true },
    },
})));

const loadUsersSuccess = on(InternalUsersActions.loadUsersSuccess, (state: PagesStore, { users }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        users,
        loaded: true,
        loading: { ...superUserState.internalUsers.loading, loading: false },
    },
})));

const invalidate = on(InternalUsersActions.invalidate, (state: PagesStore) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: { ...superUserState.internalUsers, loaded: false },
})));

const loadUsersFailure = on(InternalUsersActions.loadUsersFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        loading: { ...superUserState.internalUsers.loading, loading: false },
    },
    error,
})));

const createUserSuccess = on(InternalUsersActions.createUserSuccess, (state: PagesStore, { user }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        users: [...superUserState.internalUsers.users, user],
        loading: { ...superUserState.internalUsers.loading, saving: false },
    },
})));

const createUserFailure = on(InternalUsersActions.createUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        loading: { ...superUserState.internalUsers.loading, saving: false },
    },
    error,
})));

const updateUserSuccess = on(InternalUsersActions.updateUserSuccess, (state: PagesStore, { user }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        users: superUserState.internalUsers.users.map(u =>
            u.email?.toLowerCase() === user.email?.toLowerCase() ? user : u
        ),
        loading: { ...superUserState.internalUsers.loading, saving: false },
    },
})));

const updateUserFailure = on(InternalUsersActions.updateUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        loading: { ...superUserState.internalUsers.loading, saving: false },
    },
    error,
})));

const deleteUserSuccess = on(InternalUsersActions.deleteUserSuccess, (state: PagesStore, { email }) => withSuperUser(state, superUserState => {
    const emailLower = email.toLowerCase();
    const { [emailLower]: _removedSp, ...restSalesPersons } = superUserState.internalUsers.salesPersonsByEmail;
    const { [emailLower]: _removedSs, ...restSalesSupport } = superUserState.internalUsers.salesSupportByEmail;

    return {
        ...superUserState,
        internalUsers: {
            ...superUserState.internalUsers,
            users: superUserState.internalUsers.users.filter(u => u.email?.toLowerCase() !== emailLower),
            salesPersonsByEmail: restSalesPersons,
            salesSupportByEmail: restSalesSupport,
            loading: { ...superUserState.internalUsers.loading, deleting: false },
        },
    };
}));

const deleteUserFailure = on(InternalUsersActions.deleteUserFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        loading: { ...superUserState.internalUsers.loading, deleting: false },
    },
    error,
})));

// **************************************************************************
// COMPANIES
// **************************************************************************

const loadCompaniesSuccess = on(InternalUsersActions.loadCompaniesSuccess, (state: PagesStore, { companies }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    internalUsers: {
        ...superUserState.internalUsers,
        companies,
    },
})));

const loadCompaniesFailure = on(InternalUsersActions.loadCompaniesFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

// **************************************************************************
// SALES DATA
// **************************************************************************

const loadSalesDataSuccess = on(
    InternalUsersActions.loadSalesDataSuccess,
    (state: PagesStore, { salesPersons, salesSupport }) => withSuperUser(state, superUserState => {
        const salesPersonsByEmail: Record<string, typeof salesPersons[0]> = {};
        for (const person of salesPersons) {
            if (person.email) {
                salesPersonsByEmail[person.email.toLowerCase()] = person;
            }
        }

        const salesSupportByEmail: Record<string, typeof salesSupport[0]> = {};
        for (const person of salesSupport) {
            if (person.email) {
                salesSupportByEmail[person.email.toLowerCase()] = person;
            }
        }

        return {
            ...superUserState,
            internalUsers: {
                ...superUserState.internalUsers,
                salesPersonsByEmail,
                salesSupportByEmail,
            },
        };
    })
);

const loadSalesDataFailure = on(InternalUsersActions.loadSalesDataFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

// **************************************************************************
// SALES PERSON
// **************************************************************************

const saveSalesPersonSuccess = on(
    InternalUsersActions.saveSalesPersonSuccess,
    (state: PagesStore, { salesPerson }) => withSuperUser(state, superUserState => {
        if (!salesPerson.email) return superUserState;
        const emailLower = salesPerson.email.toLowerCase();

        return {
            ...superUserState,
            internalUsers: {
                ...superUserState.internalUsers,
                salesPersonsByEmail: {
                    ...superUserState.internalUsers.salesPersonsByEmail,
                    [emailLower]: salesPerson,
                },
            },
        };
    })
);

const saveSalesPersonFailure = on(InternalUsersActions.saveSalesPersonFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

const deleteSalesPersonSuccess = on(
    InternalUsersActions.deleteSalesPersonSuccess,
    (state: PagesStore, { email }) => withSuperUser(state, superUserState => {
        const emailLower = email.toLowerCase();
        const { [emailLower]: _removed, ...rest } = superUserState.internalUsers.salesPersonsByEmail;

        return {
            ...superUserState,
            internalUsers: {
                ...superUserState.internalUsers,
                salesPersonsByEmail: rest,
            },
        };
    })
);

const deleteSalesPersonFailure = on(InternalUsersActions.deleteSalesPersonFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

// **************************************************************************
// SALES SUPPORT
// **************************************************************************

const saveSalesSupportSuccess = on(
    InternalUsersActions.saveSalesSupportSuccess,
    (state: PagesStore, { salesSupport }) => withSuperUser(state, superUserState => {
        if (!salesSupport.email) return superUserState;
        const emailLower = salesSupport.email.toLowerCase();

        return {
            ...superUserState,
            internalUsers: {
                ...superUserState.internalUsers,
                salesSupportByEmail: {
                    ...superUserState.internalUsers.salesSupportByEmail,
                    [emailLower]: salesSupport,
                },
            },
        };
    })
);

const saveSalesSupportFailure = on(InternalUsersActions.saveSalesSupportFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

const deleteSalesSupportSuccess = on(
    InternalUsersActions.deleteSalesSupportSuccess,
    (state: PagesStore, { email }) => withSuperUser(state, superUserState => {
        const emailLower = email.toLowerCase();
        const { [emailLower]: _removed, ...rest } = superUserState.internalUsers.salesSupportByEmail;

        return {
            ...superUserState,
            internalUsers: {
                ...superUserState.internalUsers,
                salesSupportByEmail: rest,
            },
        };
    })
);

const deleteSalesSupportFailure = on(InternalUsersActions.deleteSalesSupportFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

// **************************************************************************
// LOCATIONS
// **************************************************************************

const loadLocationsSuccess = on(
    InternalUsersActions.loadLocationsSuccess,
    (state: PagesStore, { locations }) => withSuperUser(state, superUserState => ({
        ...superUserState,
        internalUsers: {
            ...superUserState.internalUsers,
            salesPersonLocations: locations,
        },
    }))
);

const loadLocationsFailure = on(InternalUsersActions.loadLocationsFailure, (state: PagesStore, { error }) => withSuperUser(state, superUserState => ({
    ...superUserState,
    error,
})));

// **************************************************************************
// LOADING STATE TRIGGERS
// **************************************************************************

const startSaving = on(
    InternalUsersActions.createUser,
    InternalUsersActions.updateUser,
    (state: PagesStore) => withSuperUser(state, superUserState => ({
        ...superUserState,
        internalUsers: {
            ...superUserState.internalUsers,
            loading: { ...superUserState.internalUsers.loading, saving: true },
        },
    }))
);

const startDeleting = on(
    InternalUsersActions.deleteUser,
    (state: PagesStore) => withSuperUser(state, superUserState => ({
        ...superUserState,
        internalUsers: {
            ...superUserState.internalUsers,
            loading: { ...superUserState.internalUsers.loading, deleting: true },
        },
    }))
);

// **************************************************************************
// EXPORT
// **************************************************************************

export const internalUsersReducer = [
    invalidate,
    // Users
    loadUsers,
    loadUsersSuccess,
    loadUsersFailure,
    startSaving,
    createUserSuccess,
    createUserFailure,
    updateUserSuccess,
    updateUserFailure,
    startDeleting,
    deleteUserSuccess,
    deleteUserFailure,
    // Companies
    loadCompaniesSuccess,
    loadCompaniesFailure,
    // Sales Data
    loadSalesDataSuccess,
    loadSalesDataFailure,
    // Sales Person
    saveSalesPersonSuccess,
    saveSalesPersonFailure,
    deleteSalesPersonSuccess,
    deleteSalesPersonFailure,
    // Sales Support
    saveSalesSupportSuccess,
    saveSalesSupportFailure,
    deleteSalesSupportSuccess,
    deleteSalesSupportFailure,
    // Locations
    loadLocationsSuccess,
    loadLocationsFailure,
];
